import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Card, DataTable, Button, Icon, useToast } from '@aidkitorg/component-library';
import { TableAction } from '@aidkitorg/component-library/dist/types/src/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { useLocalizedStrings } from '../../Localization';
import { AdminPanelPage } from '../AdminPanelPage';
import { BreadcrumbProps } from '@aidkitorg/component-library';
import type { Organization } from '@aidkitorg/aidkit/lib/program/admin/organization';
import { OrganizationModal } from './components/OrganizationModal';
import { UserInfoContext } from '../../Context';
import { useOrganizationContext } from '../../Contexts/OrganizationContext';

const DELETE_ROLES = ['admin', 'program_admin'];

export const OrganizationManagementPage: React.FC = () => {
  const L = useLocalizedStrings();
  const [search, setSearch] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { fetchOrganizations, deleteOrganization } = useOrganizationContext();

  const fetchOrganizationsRef = useRef(fetchOrganizations);

  const { toast } = useToast();
  const userInfo = useContext(UserInfoContext);

  const hasDeletePerms = useMemo(() => {
    const tags = Array.isArray(userInfo?.tags) ? userInfo.tags : [];
    return tags.some(tag => DELETE_ROLES.includes(tag));
  }, [userInfo?.tags]);

  useEffect(() => {
    fetchOrganizationsRef.current = fetchOrganizations;
  }, [fetchOrganizations]);

  useEffect(() => {
    let isCancelled = false;
    let timeoutId: number | undefined;

    const loadOrganizations = async (searchValue: string) => {
      setIsLoading(true);
      try {
        const params = searchValue ? { search: searchValue } : undefined;
        const result = await fetchOrganizationsRef.current(params);
        if (!isCancelled) {
          setOrganizations(result);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    if (!search.trim()) {
      loadOrganizations('');
    } else {
      timeoutId = window.setTimeout(() => {
        loadOrganizations(search.trim());
      }, 300);
    }

    return () => {
      isCancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      setIsLoading(false);
    };
  }, [search]);

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.user_management
    },
    {
      label: L.admin_panel.nav.organizations
    }
  ];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  const handleCreateOrganization = () => {
    setModalMode('create');
    setSelectedOrganization(null);
    setIsModalOpen(true);
  };

  const handleEditOrganization = (organization: Organization) => {
    setModalMode('edit');
    setSelectedOrganization(organization);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrganization(null);
  };

  const refreshOrganizations = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchOrganizations({ search: search.trim() || undefined });
      setOrganizations(result);
    } finally {
      setIsLoading(false);
    }
  }, [fetchOrganizations, search]);

  const handleModalSuccess = useCallback(() => {
    refreshOrganizations();
    handleCloseModal();
  }, [refreshOrganizations]);

  const pageActionButtons = [
    <Button
      key="create-organization"
      onClick={handleCreateOrganization}
      icon={<Icon name="PlusIcon" type="solid" role="decorative" />}
    >
      {L.organization.create_new_organization}
    </Button>
  ];

  const handleDeleteOrganization = useCallback(async (organization: Organization) => {
    if (!hasDeletePerms) {
      return;
    }

    const confirmTemplate = L.organization.delete_confirm;
    const confirmed = window.confirm(confirmTemplate.replace('{name}', organization.name));
    if (!confirmed) {
      return;
    }

    const result = await deleteOrganization(organization.uid);
    if (result.success) {
      toast({
        variant: 'success',
        title: L.organization.organization_deleted,
      });
      refreshOrganizations();
    } else {
      toast({
        variant: 'error',
        title: L.organization.failed_to_delete_organization,
      });
    }
  }, [L.organization.delete_confirm, L.organization.failed_to_delete_organization, L.organization.organization_deleted, deleteOrganization, hasDeletePerms, refreshOrganizations, toast]);

  return (
    <AdminPanelPage
      title={L.admin_panel.nav.organizations}
      breadcrumbs={breadcrumbs}
      pageActionButtons={pageActionButtons}
    >
      <OrganizationsList
        search={search}
        setSearch={setSearch}
        organizations={organizations}
        onEditOrganization={handleEditOrganization}
        onDeleteOrganization={handleDeleteOrganization}
        showDeleteAction={hasDeletePerms}
        loading={isLoading}
      />

      <OrganizationModal
        isOpen={isModalOpen}
        mode={modalMode}
        organization={selectedOrganization}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
      />
    </AdminPanelPage>
  );
};

type OrganizationsListProps = {
  search: string;
  setSearch: (value: string) => void;
  organizations: Organization[];
  onEditOrganization: (organization: Organization) => void;
  onDeleteOrganization: (organization: Organization) => void;
  showDeleteAction: boolean;
  loading: boolean;
};

const OrganizationsList: React.FC<OrganizationsListProps> = ({
  search,
  setSearch,
  organizations,
  onEditOrganization,
  onDeleteOrganization,
  showDeleteAction,
  loading,
}) => {
  const L = useLocalizedStrings();

  const columns = useMemo(() => {
    return [
      {
        header: L.organization.organization_name,
        accessorKey: 'name',
      },
      {
        header: L.organization.organization_description,
        accessorKey: 'description',
        cell: ({ row }) => row.original.description || '-',
      },
    ] as ColumnDef<Organization>[];
  }, [L]);

  const actions = useMemo((): TableAction<Organization>[] => {
    const baseActions: TableAction<Organization>[] = [
      {
        label: L.organization.edit_organization,
        onClick: onEditOrganization,
        icon: 'PencilIcon'
      }
    ];

    if (showDeleteAction) {
      baseActions.push({
        label: L.organization.delete_organization,
        onClick: onDeleteOrganization,
        icon: 'TrashIcon'
      });
    }

    return baseActions;
  }, [L.organization.delete_organization, L.organization.edit_organization, onDeleteOrganization, onEditOrganization, showDeleteAction]);

  return (
    <div className="w-full space-y-4">
      <Card>
        <DataTable
          columns={columns}
          data={organizations}
          actions={actions}
          enableSearch
          searchLabel={L.organization.search_organizations}
          manualSearch={{
            searchValue: search,
            onSearchChange: setSearch,
          }}
          loading={loading}
          getRowId={(row: Organization, index: number) => {
            return row.uid ?? String(index);
          }}
        />
      </Card>
    </div>
  );
};

export default OrganizationManagementPage;
